import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Grid,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  Menu,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Download,
  FileText,
  FileSpreadsheet,
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  Users,
  ListTodo,
  DollarSign,
  Flag,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Target,
  Activity,
  Printer,
  Share2,
  RefreshCw,
  Filter,
  ChevronDown,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';

import { Project, Task, TaskStatus, TaskPriority } from '@/types';
import { Risk, RiskCategory } from './RiskManagement';
import { BudgetItem, ProjectBudget } from './BudgetManagement';
import { Milestone } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

// Types
interface ProjectReportsProps {
  project: Project;
  tasks: Task[];
  budget?: ProjectBudget;
  risks?: Risk[];
  teamMembers?: Array<{ id: string; name: string; avatar?: string }>;
}

interface ReportData {
  summary: {
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    overdueTasks: number;
    completionRate: number;
    onTimeRate: number;
  };
  tasksByStatus: Record<string, number>;
  tasksByPriority: Record<string, number>;
  tasksByAssignee: Array<{ name: string; total: number; completed: number }>;
  weeklyProgress: Array<{ week: string; completed: number; created: number }>;
  budgetSummary: {
    total: number;
    spent: number;
    remaining: number;
    burnRate: number;
  };
  riskSummary: {
    total: number;
    critical: number;
    high: number;
    mitigated: number;
  };
  milestoneSummary: {
    total: number;
    completed: number;
    upcoming: number;
    overdue: number;
  };
}

// Helper functions
const getStatusLabel = (status: TaskStatus): string => {
  const labels: Record<TaskStatus, string> = {
    [TaskStatus.NOT_STARTED]: 'Not Started',
    [TaskStatus.IN_PROGRESS]: 'In Progress',
    [TaskStatus.BLOCKED]: 'Blocked',
    [TaskStatus.COMPLETED]: 'Completed',
    [TaskStatus.CANCELLED]: 'Cancelled',
  };
  return labels[status] || status;
};

const getPriorityLabel = (priority: TaskPriority): string => {
  const labels: Record<TaskPriority, string> = {
    [TaskPriority.P1]: 'Critical (P1)',
    [TaskPriority.P2]: 'High (P2)',
    [TaskPriority.P3]: 'Medium (P3)',
    [TaskPriority.P4]: 'Low (P4)',
  };
  return labels[priority] || priority;
};

const statusColors: Record<string, string> = {
  [TaskStatus.NOT_STARTED]: '#6b7280',
  [TaskStatus.IN_PROGRESS]: '#3b82f6',
  [TaskStatus.BLOCKED]: '#ef4444',
  [TaskStatus.COMPLETED]: '#22c55e',
  [TaskStatus.CANCELLED]: '#9ca3af',
};

const priorityColors: Record<string, string> = {
  [TaskPriority.P1]: '#ef4444',
  [TaskPriority.P2]: '#f59e0b',
  [TaskPriority.P3]: '#3b82f6',
  [TaskPriority.P4]: '#6b7280',
};

const ProjectReports: React.FC<ProjectReportsProps> = ({
  project,
  tasks,
  budget,
  risks = [],
  teamMembers = [],
}) => {
  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'budget' | 'risks' | 'team'>('overview');
  const [dateRange, setDateRange] = useState<{ start: Dayjs | null; end: Dayjs | null }>({
    start: dayjs().subtract(30, 'day'),
    end: dayjs(),
  });
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  // Calculate report data
  const reportData: ReportData = useMemo(() => {
    const filteredTasks = tasks.filter((t) => {
      if (!dateRange.start || !dateRange.end) return true;
      const taskDate = dayjs(t.createdAt);
      return taskDate.isAfter(dateRange.start) && taskDate.isBefore(dateRange.end.add(1, 'day'));
    });

    // Task metrics
    const totalTasks = filteredTasks.length;
    const completedTasks = filteredTasks.filter((t) => t.status === TaskStatus.COMPLETED).length;
    const inProgressTasks = filteredTasks.filter((t) => t.status === TaskStatus.IN_PROGRESS).length;
    const overdueTasks = filteredTasks.filter(
      (t) => t.status !== TaskStatus.COMPLETED && dayjs(t.dueDate).isBefore(dayjs())
    ).length;

    // Completion rate
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // On-time rate
    const completedOnTime = filteredTasks.filter(
      (t) =>
        t.status === TaskStatus.COMPLETED &&
        t.completedAt &&
        dayjs(t.completedAt).isBefore(dayjs(t.dueDate).add(1, 'day'))
    ).length;
    const onTimeRate = completedTasks > 0 ? Math.round((completedOnTime / completedTasks) * 100) : 0;

    // Tasks by status
    const tasksByStatus: Record<string, number> = {};
    Object.values(TaskStatus).forEach((status) => {
      tasksByStatus[status] = filteredTasks.filter((t) => t.status === status).length;
    });

    // Tasks by priority
    const tasksByPriority: Record<string, number> = {};
    Object.values(TaskPriority).forEach((priority) => {
      tasksByPriority[priority] = filteredTasks.filter((t) => t.priority === priority).length;
    });

    // Tasks by assignee
    const assigneeMap = new Map<string, { total: number; completed: number }>();
    filteredTasks.forEach((task) => {
      const assignees = task.raci?.responsible || [];
      assignees.forEach((userId) => {
        const current = assigneeMap.get(userId) || { total: 0, completed: 0 };
        current.total++;
        if (task.status === TaskStatus.COMPLETED) current.completed++;
        assigneeMap.set(userId, current);
      });
    });

    const tasksByAssignee = Array.from(assigneeMap.entries()).map(([userId, data]) => ({
      name: teamMembers.find((m) => m.id === userId)?.name || userId,
      ...data,
    })).sort((a, b) => b.total - a.total);

    // Weekly progress (last 8 weeks)
    const weeklyProgress: Array<{ week: string; completed: number; created: number }> = [];
    for (let i = 7; i >= 0; i--) {
      const weekStart = dayjs().subtract(i, 'week').startOf('week');
      const weekEnd = weekStart.endOf('week');
      const weekLabel = weekStart.format('MMM D');

      const created = tasks.filter(
        (t) => dayjs(t.createdAt).isAfter(weekStart) && dayjs(t.createdAt).isBefore(weekEnd)
      ).length;

      const completed = tasks.filter(
        (t) =>
          t.completedAt &&
          dayjs(t.completedAt).isAfter(weekStart) &&
          dayjs(t.completedAt).isBefore(weekEnd)
      ).length;

      weeklyProgress.push({ week: weekLabel, completed, created });
    }

    // Budget summary
    const budgetItems = budget?.items || [];
    const totalBudget = budget?.totalBudget || 0;
    const spent = budgetItems.reduce((sum, item) => sum + (item.type === 'expense' ? item.actualAmount : 0), 0);
    const remaining = totalBudget - spent;
    const burnRate = totalBudget > 0 ? (spent / totalBudget) * 100 : 0;

    // Risk summary
    const getRiskScore = (risk: Risk) => risk.probability * risk.impact;
    const critical = risks.filter((r) => getRiskScore(r) >= 15).length;
    const high = risks.filter((r) => getRiskScore(r) >= 10 && getRiskScore(r) < 15).length;
    const mitigated = risks.filter((r) => r.status === 'resolved' || r.status === 'closed').length;

    // Milestone summary
    const milestones = project.milestones || [];
    const completedMilestones = milestones.filter((m) => m.isCompleted).length;
    const upcomingMilestones = milestones.filter(
      (m) => !m.isCompleted && dayjs(m.dueDate).isAfter(dayjs())
    ).length;
    const overdueMilestones = milestones.filter(
      (m) => !m.isCompleted && dayjs(m.dueDate).isBefore(dayjs())
    ).length;

    return {
      summary: {
        totalTasks,
        completedTasks,
        inProgressTasks,
        overdueTasks,
        completionRate,
        onTimeRate,
      },
      tasksByStatus,
      tasksByPriority,
      tasksByAssignee,
      weeklyProgress,
      budgetSummary: {
        total: totalBudget,
        spent,
        remaining,
        burnRate,
      },
      riskSummary: {
        total: risks.length,
        critical,
        high,
        mitigated,
      },
      milestoneSummary: {
        total: milestones.length,
        completed: completedMilestones,
        upcoming: upcomingMilestones,
        overdue: overdueMilestones,
      },
    };
  }, [tasks, budget, risks, project.milestones, dateRange, teamMembers]);

  // Export handlers
  const handleExport = async (format: 'pdf' | 'csv' | 'excel') => {
    setIsExporting(true);
    setExportMenuAnchor(null);

    try {
      // Simulate export delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (format === 'csv') {
        // Generate CSV
        const csvData = generateCSV();
        downloadFile(csvData, `project-report-${project.name}.csv`, 'text/csv');
      } else if (format === 'excel') {
        // For Excel, we'd typically use a library like xlsx
        const csvData = generateCSV();
        downloadFile(csvData, `project-report-${project.name}.csv`, 'text/csv');
      } else {
        // For PDF, we'd use a library like jspdf or react-pdf
        alert('PDF export would be implemented with jspdf or similar library');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const generateCSV = (): string => {
    const rows: string[][] = [
      ['Project Report', project.name],
      ['Generated', dayjs().format('MMMM D, YYYY h:mm A')],
      [''],
      ['SUMMARY'],
      ['Total Tasks', reportData.summary.totalTasks.toString()],
      ['Completed Tasks', reportData.summary.completedTasks.toString()],
      ['In Progress', reportData.summary.inProgressTasks.toString()],
      ['Overdue', reportData.summary.overdueTasks.toString()],
      ['Completion Rate', formatPercent(reportData.summary.completionRate)],
      ['On-Time Rate', formatPercent(reportData.summary.onTimeRate)],
      [''],
      ['TASKS BY STATUS'],
      ...Object.entries(reportData.tasksByStatus).map(([status, count]) => [
        getStatusLabel(status as TaskStatus),
        count.toString(),
      ]),
      [''],
      ['TASKS BY PRIORITY'],
      ...Object.entries(reportData.tasksByPriority).map(([priority, count]) => [
        getPriorityLabel(priority as TaskPriority),
        count.toString(),
      ]),
      [''],
      ['TASKS BY ASSIGNEE'],
      ['Name', 'Total', 'Completed'],
      ...reportData.tasksByAssignee.map((a) => [a.name, a.total.toString(), a.completed.toString()]),
    ];

    return rows.map((row) => row.join(',')).join('\n');
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: budget?.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box ref={reportRef}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Project Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analytics and insights for {project.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={isExporting ? <CircularProgress size={16} /> : <Download size={16} />}
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            disabled={isExporting}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Printer size={16} />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Date Range Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Date Range:
            </Typography>
            <DatePicker
              label="From"
              value={dateRange.start}
              onChange={(date) => setDateRange({ ...dateRange, start: date })}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <Typography variant="body2" color="text.secondary">to</Typography>
            <DatePicker
              label="To"
              value={dateRange.end}
              onChange={(date) => setDateRange({ ...dateRange, end: date })}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <Button
              size="small"
              onClick={() => setDateRange({ start: dayjs().subtract(30, 'day'), end: dayjs() })}
            >
              Last 30 Days
            </Button>
            <Button
              size="small"
              onClick={() => setDateRange({ start: dayjs().subtract(90, 'day'), end: dayjs() })}
            >
              Last 90 Days
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab value="overview" label="Overview" icon={<BarChart3 size={16} />} iconPosition="start" />
        <Tab value="tasks" label="Tasks" icon={<ListTodo size={16} />} iconPosition="start" />
        <Tab value="budget" label="Budget" icon={<DollarSign size={16} />} iconPosition="start" />
        <Tab value="risks" label="Risks" icon={<AlertTriangle size={16} />} iconPosition="start" />
        <Tab value="team" label="Team" icon={<Users size={16} />} iconPosition="start" />
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <Box>
          {/* Key Metrics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {reportData.summary.totalTasks}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Tasks
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#dcfce7' }}>
                <Typography variant="h4" fontWeight={700} color="#166534">
                  {reportData.summary.completedTasks}
                </Typography>
                <Typography variant="caption" color="#166534">
                  Completed
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#dbeafe' }}>
                <Typography variant="h4" fontWeight={700} color="#1e40af">
                  {reportData.summary.inProgressTasks}
                </Typography>
                <Typography variant="caption" color="#1e40af">
                  In Progress
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fee2e2' }}>
                <Typography variant="h4" fontWeight={700} color="#991b1b">
                  {reportData.summary.overdueTasks}
                </Typography>
                <Typography variant="caption" color="#991b1b">
                  Overdue
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdf4' }}>
                <Typography variant="h4" fontWeight={700} color="#166534">
                  {formatPercent(reportData.summary.completionRate)}
                </Typography>
                <Typography variant="caption" color="#166534">
                  Completion Rate
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fefce8' }}>
                <Typography variant="h4" fontWeight={700} color="#854d0e">
                  {formatPercent(reportData.summary.onTimeRate)}
                </Typography>
                <Typography variant="caption" color="#854d0e">
                  On-Time Rate
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3}>
            {/* Tasks by Status */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Tasks by Status
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(reportData.tasksByStatus).map(([status, count]) => {
                      const percentage = reportData.summary.totalTasks > 0
                        ? Math.round((count / reportData.summary.totalTasks) * 100)
                        : 0;
                      return (
                        <Box key={status} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{getStatusLabel(status as TaskStatus)}</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {count} ({formatPercent(percentage)})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#f3f4f6',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: statusColors[status],
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Tasks by Priority */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Tasks by Priority
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(reportData.tasksByPriority).map(([priority, count]) => {
                      const percentage = reportData.summary.totalTasks > 0
                        ? Math.round((count / reportData.summary.totalTasks) * 100)
                        : 0;
                      return (
                        <Box key={priority} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{getPriorityLabel(priority as TaskPriority)}</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {count} ({formatPercent(percentage)})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#f3f4f6',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: priorityColors[priority],
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Weekly Progress */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Weekly Progress (Last 8 Weeks)
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    {reportData.weeklyProgress.map((week) => (
                      <Box key={week.week} sx={{ flex: 1, textAlign: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              height: Math.max(week.completed * 8, 4),
                              bgcolor: '#22c55e',
                              borderRadius: '4px 4px 0 0',
                            }}
                          />
                          <Box
                            sx={{
                              width: '100%',
                              height: Math.max(week.created * 8, 4),
                              bgcolor: '#3b82f6',
                              borderRadius: '0 0 4px 4px',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {week.week}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" display="block" color="#22c55e">
                            ✓ {week.completed}
                          </Typography>
                          <Typography variant="caption" display="block" color="#3b82f6">
                            + {week.created}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#22c55e', borderRadius: 1 }} />
                      <Typography variant="caption">Completed</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#3b82f6', borderRadius: 1 }} />
                      <Typography variant="caption">Created</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Summary Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DollarSign size={20} color="#0284c7" />
                    <Typography variant="subtitle2">Budget</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#0284c7">
                    {formatCurrency(reportData.budgetSummary.spent)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {formatCurrency(reportData.budgetSummary.total)} ({formatPercent(reportData.budgetSummary.burnRate)})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AlertTriangle size={20} color="#d97706" />
                    <Typography variant="subtitle2">Risks</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#d97706">
                    {reportData.riskSummary.critical + reportData.riskSummary.high}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    High/Critical of {reportData.riskSummary.total} total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Flag size={20} color="#16a34a" />
                    <Typography variant="subtitle2">Milestones</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#16a34a">
                    {reportData.milestoneSummary.completed}/{reportData.milestoneSummary.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Completed ({reportData.milestoneSummary.overdue} overdue)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#faf5ff', border: '1px solid #e9d5ff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Users size={20} color="#9333ea" />
                    <Typography variant="subtitle2">Team</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#9333ea">
                    {teamMembers.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active members
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Tasks by Assignee
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell>Team Member</TableCell>
                  <TableCell align="center">Total Tasks</TableCell>
                  <TableCell align="center">Completed</TableCell>
                  <TableCell align="center">Completion Rate</TableCell>
                  <TableCell>Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.tasksByAssignee.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No task assignments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.tasksByAssignee.map((assignee) => {
                    const rate = assignee.total > 0
                      ? Math.round((assignee.completed / assignee.total) * 100)
                      : 0;
                    const member = teamMembers.find((m) => m.name === assignee.name);
                    
                    return (
                      <TableRow key={assignee.name} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar src={member?.avatar} sx={{ width: 32, height: 32 }}>
                              {assignee.name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>
                              {assignee.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{assignee.total}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            {assignee.completed}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatPercent(rate)}
                            size="small"
                            sx={{
                              bgcolor: rate >= 80 ? '#dcfce7' : rate >= 50 ? '#fef3c7' : '#fee2e2',
                              color: rate >= 80 ? '#166534' : rate >= 50 ? '#92400e' : '#991b1b',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 200 }}>
                          <LinearProgress
                            variant="determinate"
                            value={rate}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#f3f4f6',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: rate >= 80 ? '#22c55e' : rate >= 50 ? '#f59e0b' : '#ef4444',
                                borderRadius: 4,
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Budget Tab */}
      {activeTab === 'budget' && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Budget
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {formatCurrency(reportData.budgetSummary.total)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: reportData.budgetSummary.burnRate > 100 ? '#fee2e2' : '#f0fdf4' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Spent
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color={reportData.budgetSummary.burnRate > 100 ? '#dc2626' : '#16a34a'}
                  >
                    {formatCurrency(reportData.budgetSummary.spent)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatPercent(reportData.budgetSummary.burnRate)} of budget
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Remaining
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color={reportData.budgetSummary.remaining < 0 ? '#dc2626' : 'text.primary'}
                  >
                    {formatCurrency(reportData.budgetSummary.remaining)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Risks Tab */}
      {activeTab === 'risks' && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700}>
                  {reportData.riskSummary.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Risks
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fee2e2' }}>
                <Typography variant="h4" fontWeight={700} color="#dc2626">
                  {reportData.riskSummary.critical}
                </Typography>
                <Typography variant="caption" color="#dc2626">
                  Critical
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffedd5' }}>
                <Typography variant="h4" fontWeight={700} color="#c2410c">
                  {reportData.riskSummary.high}
                </Typography>
                <Typography variant="caption" color="#c2410c">
                  High
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#dcfce7' }}>
                <Typography variant="h4" fontWeight={700} color="#166534">
                  {reportData.riskSummary.mitigated}
                </Typography>
                <Typography variant="caption" color="#166534">
                  Mitigated
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Team Performance
          </Typography>
          <Grid container spacing={2}>
            {teamMembers.map((member) => {
              const memberTasks = reportData.tasksByAssignee.find((a) => a.name === member.name);
              const completionRate = memberTasks && memberTasks.total > 0
                ? Math.round((memberTasks.completed / memberTasks.total) * 100)
                : 0;
              
              return (
                <Grid item xs={12} sm={6} md={4} key={member.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar src={member.avatar} sx={{ width: 48, height: 48 }}>
                          {member.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {member.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {memberTasks?.total || 0} tasks assigned
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Completion Rate
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {formatPercent(completionRate)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={completionRate}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#f3f4f6',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: completionRate >= 80 ? '#22c55e' : completionRate >= 50 ? '#f59e0b' : '#ef4444',
                            borderRadius: 3,
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExport('csv')}>
          <FileSpreadsheet size={16} style={{ marginRight: 8 }} />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={() => handleExport('excel')}>
          <FileSpreadsheet size={16} style={{ marginRight: 8 }} />
          Export as Excel
        </MenuItem>
        <MenuItem onClick={() => handleExport('pdf')}>
          <FileText size={16} style={{ marginRight: 8 }} />
          Export as PDF
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProjectReports;
